import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { ArrowDown, ArrowUp, RotateCcw, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCalls, type CallSortField, type SortOrder } from '../api/telemetry'
import { useCallPurposes } from '../api/callPurposes'
import { StatusBadge } from '../components/shared/StatusBadge'
import { DirectionBadge } from '../components/shared/DirectionBadge'
import { OutcomeBadge } from '../components/shared/OutcomeBadge'
import { useOutcomeOptions } from '../components/shared/outcomeMeta'
import { Pagination } from '../components/shared/Pagination'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { formatPhone, formatDuration, formatDateTime } from '../lib/utils'

const PAGE_SIZE = 25

type Filters = {
  status: string
  direction: string
  outcome: string
  purpose: string
  phone: string
  startedAfter: string  // YYYY-MM-DD
  startedBefore: string  // YYYY-MM-DD
  sort: CallSortField
  order: SortOrder
}

const EMPTY: Filters = {
  status: '',
  direction: '',
  outcome: '',
  purpose: '',
  phone: '',
  startedAfter: '',
  startedBefore: '',
  sort: 'started_at',
  order: 'desc',
}

function dateInputToUnix(value: string, endOfDay: boolean): number | undefined {
  if (!value) return undefined
  const ts = Date.parse(value + (endOfDay ? 'T23:59:59' : 'T00:00:00'))
  return Number.isNaN(ts) ? undefined : ts / 1000
}

export function CallHistoryPage() {
  const { t } = useTranslation('calls')
  const navigate = useNavigate()
  const [filters, setFilters] = useState<Filters>(EMPTY)
  const [phoneInput, setPhoneInput] = useState('')
  const [offset, setOffset] = useState(0)
  const { data: purposeData } = useCallPurposes()
  const purposeLabels = useMemo(() => {
    const map: Record<string, string> = {}
    for (const p of purposeData?.outbound ?? []) map[p.purpose] = p.label
    for (const r of purposeData?.inbound_reasons ?? []) map[r.reason] = r.label
    return map
  }, [purposeData])

  // Debounce phone input → filter (~300ms) without triggering on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.phone === phoneInput ? f : { ...f, phone: phoneInput }))
      setOffset(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [phoneInput])

  const queryParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset,
      status: filters.status || undefined,
      direction: filters.direction || undefined,
      outcome: filters.outcome || undefined,
      purpose: filters.purpose || undefined,
      phone: filters.phone || undefined,
      started_after: dateInputToUnix(filters.startedAfter, false),
      started_before: dateInputToUnix(filters.startedBefore, true),
      sort: filters.sort,
      order: filters.order,
    }),
    [filters, offset],
  )

  const { data, isLoading, isError, refetch } = useCalls(queryParams)

  const update = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }))
    setOffset(0)
  }

  const reset = () => {
    setFilters(EMPTY)
    setPhoneInput('')
    setOffset(0)
  }

  const calls = data?.calls ?? []
  const total = data?.total ?? 0
  const hasActiveFilters =
    filters.status ||
    filters.direction ||
    filters.outcome ||
    filters.purpose ||
    filters.phone ||
    filters.startedAfter ||
    filters.startedBefore ||
    filters.sort !== 'started_at' ||
    filters.order !== 'desc'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-text-primary md:text-2xl">{t('history.title')}</h1>
        {total > 0 && (
          <span className="text-sm text-text-muted">
            {t('history.totalCount', { count: total })}
          </span>
        )}
      </div>

      <Filters
        filters={filters}
        phoneInput={phoneInput}
        onPhoneInputChange={setPhoneInput}
        onChange={update}
        onReset={reset}
        showReset={!!hasActiveFilters}
        purposeOptions={purposeData}
      />

      {isLoading ? (
        <SpinLoader label={t('history.loadingAll')} />
      ) : isError ? (
        <ErrorCard message={t('history.loadFailed')} onRetry={() => refetch()} />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-elevated text-xs text-text-muted">
                <tr>
                  <th className="px-4 py-2">{t('history.table.time')}</th>
                  <th className="px-4 py-2">{t('history.table.phone')}</th>
                  <th className="px-4 py-2">{t('history.table.direction')}</th>
                  <th className="px-4 py-2">{t('history.table.purpose')}</th>
                  <th className="px-4 py-2">{t('history.table.status')}</th>
                  <th className="px-4 py-2">{t('history.table.outcome')}</th>
                  <th className="px-4 py-2">{t('history.table.duration')}</th>
                  <th className="px-4 py-2">{t('history.table.charge')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {calls.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-text-muted">
                      {t('history.noResults')}
                    </td>
                  </tr>
                ) : (
                  calls.map((call) => {
                    const phone = call.direction === 'outbound' ? call.to_number : call.from_number
                    const charge = call.price_usd ?? call.cost_usd
                    return (
                      <tr
                        key={call.call_sid}
                        onClick={() => navigate(`/app/calls/${call.call_sid}`)}
                        className="cursor-pointer bg-surface transition-colors hover:bg-surface-elevated"
                      >
                        <td className="whitespace-nowrap px-4 py-2 text-text-secondary">
                          {formatDateTime(call.started_at)}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/app/calls/stage/${encodeURIComponent(phone)}`)
                            }}
                            className="font-medium text-primary hover:underline"
                          >
                            {formatPhone(phone)}
                          </button>
                        </td>
                        <td className="px-4 py-2">
                          <DirectionBadge direction={call.direction} />
                        </td>
                        <td className="px-4 py-2 text-text-secondary">
                          {call.purpose ? (purposeLabels[call.purpose] ?? call.purpose) : <span className="text-text-muted">—</span>}
                        </td>
                        <td className="px-4 py-2">
                          <StatusBadge status={call.status} />
                        </td>
                        <td className="px-4 py-2">
                          <OutcomeBadge outcome={call.outcome} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-text-secondary">
                          {formatDuration(call.duration_s)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-text-secondary">
                          {charge != null ? `$${charge.toFixed(4)}` : '—'}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card layout */}
          <div className="space-y-2 md:hidden">
            {calls.length === 0 ? (
              <p className="py-8 text-center text-text-muted">{t('history.noResults')}</p>
            ) : (
              calls.map((call) => {
                const phone = call.direction === 'outbound' ? call.to_number : call.from_number
                const charge = call.price_usd ?? call.cost_usd
                return (
                  <div
                    key={call.call_sid}
                    onClick={() => navigate(`/app/calls/${call.call_sid}`)}
                    className="cursor-pointer rounded-lg border border-border bg-surface p-3 transition-colors hover:bg-surface-elevated"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/app/calls/stage/${encodeURIComponent(phone)}`)
                        }}
                        className="font-medium text-primary hover:underline"
                      >
                        {formatPhone(phone)}
                      </button>
                      <StatusBadge status={call.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-text-secondary">
                      <DirectionBadge direction={call.direction} />
                      {call.purpose && (
                        <span className="rounded-full bg-text-muted/15 px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                          {purposeLabels[call.purpose] ?? call.purpose}
                        </span>
                      )}
                      <OutcomeBadge outcome={call.outcome} size="sm" />
                      <span className="font-mono">{formatDuration(call.duration_s)}</span>
                      {charge != null && <span className="font-mono">${charge.toFixed(4)}</span>}
                      <span className="ml-auto">{formatDateTime(call.started_at)}</span>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          <Pagination offset={offset} limit={PAGE_SIZE} total={total} onPageChange={setOffset} />
        </>
      )}
    </div>
  )
}

function Filters({
  filters,
  phoneInput,
  onPhoneInputChange,
  onChange,
  onReset,
  showReset,
  purposeOptions,
}: {
  filters: Filters
  phoneInput: string
  onPhoneInputChange: (v: string) => void
  onChange: <K extends keyof Filters>(key: K, value: Filters[K]) => void
  onReset: () => void
  showReset: boolean
  purposeOptions: { outbound: { purpose: string; label: string }[]; inbound_reasons: { reason: string; label: string }[] } | undefined
}) {
  const { t } = useTranslation('calls')
  const outcomeOptions = useOutcomeOptions()
  const purposeSelectOptions = [
    { value: '', label: t('history.filters.all') },
    ...(purposeOptions?.outbound ?? []).map((p) => ({
      value: p.purpose,
      label: t('history.filters.outboundPrefix', { label: p.label }),
    })),
    ...(purposeOptions?.inbound_reasons ?? []).map((r) => ({
      value: r.reason,
      label: t('history.filters.inboundPrefix', { label: r.label }),
    })),
  ]
  return (
    <div className="rounded-lg border border-border bg-surface-card p-3">
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <Select
          label={t('history.filters.status')}
          value={filters.status}
          onChange={(v) => onChange('status', v)}
          options={[
            { value: '', label: t('history.filters.all') },
            { value: 'active', label: t('status.active') },
            { value: 'ended', label: t('status.ended') },
            { value: 'error', label: t('status.error') },
          ]}
        />
        <Select
          label={t('history.filters.direction')}
          value={filters.direction}
          onChange={(v) => onChange('direction', v)}
          options={[
            { value: '', label: t('history.filters.all') },
            { value: 'inbound', label: t('direction.inbound') },
            { value: 'outbound', label: t('direction.outbound') },
            { value: 'browser', label: t('direction.browser') },
          ]}
        />
        <Select
          label={t('history.filters.outcome')}
          value={filters.outcome}
          onChange={(v) => onChange('outcome', v)}
          options={[
            { value: '', label: t('history.filters.all') },
            ...outcomeOptions.map(({ value, label }) => ({ value, label })),
          ]}
        />
        <Select
          label={t('history.filters.purpose')}
          value={filters.purpose}
          onChange={(v) => onChange('purpose', v)}
          options={purposeSelectOptions}
        />
        <div>
          <label className="text-xs font-medium text-text-muted">{t('history.filters.phone')}</label>
          <div className="relative mt-0.5">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
            <input
              type="search"
              value={phoneInput}
              onChange={(e) => onPhoneInputChange(e.target.value)}
              placeholder={t('history.filters.phonePlaceholder')}
              className="w-full rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        <DateInput
          label={t('history.filters.from')}
          value={filters.startedAfter}
          onChange={(v) => onChange('startedAfter', v)}
        />
        <DateInput
          label={t('history.filters.to')}
          value={filters.startedBefore}
          onChange={(v) => onChange('startedBefore', v)}
        />
        <Select
          label={t('history.filters.sort')}
          value={filters.sort}
          onChange={(v) => onChange('sort', v as CallSortField)}
          options={[
            { value: 'started_at', label: t('history.filters.sortBy.time') },
            { value: 'duration_s', label: t('history.filters.sortBy.duration') },
          ]}
        />
        <div className="flex items-end gap-2">
          <button
            onClick={() => onChange('order', filters.order === 'desc' ? 'asc' : 'desc')}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
            aria-label={
              filters.order === 'desc'
                ? t('history.filters.sortDescending')
                : t('history.filters.sortAscending')
            }
          >
            {filters.order === 'desc' ? (
              <>
                <ArrowDown className="h-3.5 w-3.5" /> {t('history.filters.desc')}
              </>
            ) : (
              <>
                <ArrowUp className="h-3.5 w-3.5" /> {t('history.filters.asc')}
              </>
            )}
          </button>
          {showReset && (
            <button
              onClick={onReset}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
            >
              <RotateCcw className="h-3.5 w-3.5" /> {t('history.filters.reset')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div>
      <label className="text-xs font-medium text-text-muted">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="text-xs font-medium text-text-muted">{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
    </div>
  )
}
